// Per-tenant Stripe billing actions -- lives in the Tenants & Modules
// tab's detail panel, next to Platform Billing Basis. Two manual steps
// (no automatic cron yet -- see run-tenant-billing's own header for why):
// create the tenant's Stripe Customer once, then trigger a billing run
// whenever you want to charge them for the current month. Recent runs
// list below double as this tenant's own receipt history.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { CreditCard, Receipt, Play } from 'lucide-react';
import { logPlatformAdminAction } from '@/lib/platformAuditLog';

interface SuperadminTenantBillingProps {
  tenantId: string;
  stripeCustomerId: string | null;
  paymentRestricted: boolean;
  onStripeCustomerCreated: (stripeCustomerId: string) => void;
}

interface BillingPeriodRow {
  id: string;
  period_start: string;
  total: number;
  status: 'pending' | 'paid' | 'failed' | 'uncollectible' | 'void';
  created_at: string;
}

const STATUS_STYLES: Record<BillingPeriodRow['status'], string> = {
  paid: 'bg-green-900/20 border-green-900/40 text-green-400',
  pending: 'bg-yellow-900/20 border-yellow-900/40 text-yellow-500',
  failed: 'bg-red-900/20 border-red-900/40 text-red-300',
  uncollectible: 'bg-red-900/20 border-red-900/40 text-red-300',
  void: 'bg-gray-700/40 border-gray-600 text-gray-300',
};

const SuperadminTenantBilling: React.FC<SuperadminTenantBillingProps> = ({
  tenantId,
  stripeCustomerId,
  paymentRestricted,
  onStripeCustomerCreated,
}) => {
  const [periods, setPeriods] = useState<BillingPeriodRow[]>([]);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [runningBilling, setRunningBilling] = useState(false);

  const loadPeriods = async () => {
    setLoadingPeriods(true);
    const { data, error } = await supabase
      .from('billing_periods')
      .select('id, period_start, total, status, created_at')
      .eq('tenant_id', tenantId)
      .order('period_start', { ascending: false })
      .limit(6);
    if (error) {
      toast({ title: 'Error', description: 'Failed to load billing history: ' + error.message, variant: 'destructive' });
    } else {
      setPeriods((data as BillingPeriodRow[]) || []);
    }
    setLoadingPeriods(false);
  };

  useEffect(() => {
    loadPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const createStripeCustomer = async () => {
    setCreatingCustomer(true);
    const { data, error } = await supabase.functions.invoke('create-stripe-customer', { body: { tenant_id: tenantId } });
    setCreatingCustomer(false);
    if (error || data?.error) {
      toast({ title: 'Error', description: data?.error || error?.message || 'Failed to create Stripe customer', variant: 'destructive' });
      return;
    }
    toast({ title: 'Stripe customer ready', description: data.created ? 'Created a new Stripe customer.' : 'Already had one.' });
    onStripeCustomerCreated(data.stripe_customer_id);
    logPlatformAdminAction('stripe_customer_created', 'tenant', tenantId, { stripe_customer_id: data.stripe_customer_id });
  };

  const runBilling = async () => {
    setRunningBilling(true);
    const { data, error } = await supabase.functions.invoke('run-tenant-billing', { body: { tenant_id: tenantId } });
    setRunningBilling(false);
    if (error || data?.error) {
      toast({ title: 'Error', description: data?.error || error?.message || 'Failed to run billing', variant: 'destructive' });
      return;
    }
    toast({
      title: 'Billing run complete',
      description: `$${Number(data.total).toFixed(2)} -- invoice status: ${data.status}`,
    });
    logPlatformAdminAction('tenant_billing_run', 'tenant', tenantId, { total: data.total, status: data.status });
    loadPeriods();
  };

  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3.5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-medium text-yellow-500 uppercase tracking-wide flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" />
            Stripe Billing
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {stripeCustomerId ? `Customer ${stripeCustomerId}` : 'No Stripe customer yet'}
            {paymentRestricted && <span className="text-red-400 font-medium"> -- payment restricted</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!stripeCustomerId ? (
            <Button size="sm" className="golden-button" onClick={createStripeCustomer} disabled={creatingCustomer}>
              {creatingCustomer ? 'Creating...' : 'Create Stripe Customer'}
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={runBilling} disabled={runningBilling}>
              <Play className="h-3.5 w-3.5 mr-1.5" />
              {runningBilling ? 'Running...' : 'Run Billing Now'}
            </Button>
          )}
        </div>
      </div>

      {!loadingPeriods && periods.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <Receipt className="h-3 w-3" />
            Recent billing runs
          </p>
          {periods.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 text-xs px-2.5 py-1.5 rounded bg-background/40">
              <span className="text-muted-foreground">
                {new Date(p.period_start).toLocaleDateString('en-NZ', { month: 'short', year: 'numeric' })}
              </span>
              <span className="font-semibold text-foreground tabular-nums">${p.total.toFixed(2)}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLES[p.status]}`}>{p.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuperadminTenantBilling;
